import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { ScanEye, Loader2 } from 'lucide-react';
import { api, errMsg } from '@/lib/api';

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await register(email.trim(), password, name.trim() || 'User');
      toast.success('Account created — welcome!');
      // If user came from a paid plan CTA, auto-start checkout
      let pending = null;
      try { pending = localStorage.getItem('ageorush_pending_plan'); } catch (_e) { /* noop */ }
      if (pending && (pending === 'try' || pending === 'starter' || pending === 'growth' || pending === 'enterprise')) {
        try { localStorage.removeItem('ageorush_pending_plan'); } catch (_e) { /* noop */ }
        try {
          const r = await api.post('/billing/checkout', { plan: pending });
          if (r.data?.checkout_url) {
            window.location.href = r.data.checkout_url;
            return;
          }
        } catch (err) {
          const detail = errMsg(err, '');
          if (detail) toast.message('Continuing on Free — checkout unavailable', { description: detail });
        }
      }
      navigate('/app/dashboard');
    } catch (err) {
      toast.error(errMsg(err, 'Registration failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col hero-mist">
      <header className="mx-auto w-full max-w-[1240px] px-4 sm:px-6 lg:px-8 h-16 flex items-center">
        <Link to="/" className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]"><ScanEye className="h-5 w-5" /></div>
          <div className="font-display text-base font-semibold">GEOrush</div>
        </Link>
      </header>
      <div className="flex-1 flex items-center justify-center px-4">
        <Card className="w-full max-w-md border card-elevated bg-card">
          <div className="p-6 sm:p-8">
            <h1 className="font-display text-2xl font-semibold tracking-tight">Create your account</h1>
            <p className="mt-1 text-sm text-muted-foreground">Start tracking your AI visibility in minutes.</p>
            <form onSubmit={onSubmit} className="mt-6 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="name">Full name</Label>
                <Input id="name" required value={name} onChange={(e) => setName(e.target.value)} data-testid="auth-register-name-input" placeholder="Alex Marketer" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="email">Work email</Label>
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="auth-register-email-input" placeholder="you@brand.com" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="auth-register-password-input" placeholder="At least 6 characters" />
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="auth-register-submit-button">
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create account
              </Button>
            </form>
            <div className="mt-6 text-center text-sm text-muted-foreground">
              Already have an account?{' '}
              <Link to="/login" className="text-[hsl(var(--accent))] font-medium hover:underline" data-testid="auth-register-to-login-link">Sign in</Link>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
